/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {get} from '@ion/core/src/environment/environment';
import {initializeLanguageAsync} from '@ion/core/src/locale/locale';
import {error, info} from '@ion/core/src/logger/logger';
import Metrics from '@ion/core/src/performance/Metrics';
import {
  getSubAccounts
} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {getToken} from '@ion/core/src/services/SDConnectUtils';
import {
  Account,
  Asset,
  CVSAssetType,
  GenericObject,
  User
} from '@ion/core/src/types/entities';
import AssetObserver from '@ion/services/src/websocket/AssetObserver';
import EntityObserver from '@ion/services/src/websocket/EntityObserver';
import changePage, {getRoute, Route} from '@/routes/RouteDefinitions';
import {assetUpdated} from '@/store/actions/dashboard';
import {addErrorInterceptor} from '@/store/actions/errors';
import {showEULADialog} from '@/store/actions/login';
import {
  initializePageSecurity,
  setPage
} from '@/store/actions/nav';
import '../../props/GlobalProps';
import {Language} from '../../types/application';

export const PRE_INITIALIZATION_REQUEST          = 'PRE_INITIALIZATION_REQUEST';
export const PRE_INITIALIZATION_COMPLETE         = 'PRE_INITIALIZATION_COMPLETE';
export const PRE_INITIALIZATION_ERROR            = 'PRE_INITIALIZATION_ERROR';

export const APPLICATION_INITIALIZATION_REQUEST  = 'APPLICATION_INITIALIZATION_REQUEST';
export const APPLICATION_INITIALIZATION_COMPLETE = 'APPLICATION_INITIALIZATION_COMPLETE';
export const APPLICATION_INITIALIZATION_ERROR    = 'APPLICATION_INITIALIZATION_ERROR';

export const POST_INITIALIZATION_REQUEST         = 'POST_INITIALIZATION_REQUEST';
export const POST_INITIALIZATION_COMPLETE        = 'POST_INITIALIZATION_COMPLETE';
export const POST_INITIALIZATION_ERROR           = 'POST_INITIALIZATION_ERROR';

export const REFRESH_INITIALIZATION_REQUEST      = 'REFRESH_INITIALIZATION_REQUEST';
export const REFRESH_INITIALIZATION_COMPLETE     = 'REFRESH_INITIALIZATION_COMPLETE';
export const REFRESH_INITIALIZATION_ERROR        = 'REFRESH_INITIALIZATION_ERROR';

export const INSUFFICIENT_PERMISSIONS_ERROR      = 'INSUFFICIENT_PERMISSIONS_ERROR';

// provides the application perceived performance metrics ...
const metrics = new Metrics();

export const metricsBegin = () => {
  metrics.start();
  info('Metrics [Authentication & Application Initialization]: Started ...');
};

export const metricsEnd = () => {
  if (metrics.isRunning()) {
    info(`Metrics [Authentication & Application Initialization]: Completed in ${metrics.stop().results(true, false)} seconds`);
  }
};

/**
 * Provides the application pre-authentication initialization action. This
 * action manages all pre-initialization configurations required prior to
 * authentication.
 */
export const preinitialization = () => {
  info('Application Pre Initialization ...');
  return dispatch => {
    addErrorInterceptor(dispatch);
    info('Application Pre-initialization Complete');
    dispatch({type: PRE_INITIALIZATION_COMPLETE});
  };
};

/**
 * Provides the application post-authentication initialization action. This
 * action manages all post-authentication initialization requests required
 * by the application prior to rendering the first page.
 */
export const initialize = (user: User, account: Account, route?: any) => {
  return async dispatch => {
    info('Application Initialization ...');
    await initializeLanguageAsync(Language[account?.language]);
    dispatch({type: APPLICATION_INITIALIZATION_REQUEST, route});
    initializeEntityObserver();
    try {
      if (user.eulaAccepted) {
        dispatch(initializationComplete({user}, route));
      } else {
        dispatch(showEULADialog(true));
      }
    } catch (err) {
      dispatch({type: APPLICATION_INITIALIZATION_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Defines the initialization complete action which is invoked upon successful
 * initialization of all post-authentication service calls, after which, the
 * first page is ready to be rendered.
 */
export const initializationComplete = (items, route = null) => {
  return async(dispatch, getState) => {
    info('Application Initialization Complete');
    dispatch({type: APPLICATION_INITIALIZATION_COMPLETE, ...items, route});
    let defaultPage = getRoute(getState().nav.defaultPage);
    const currentPage = getRoute(getState().nav.currentPage);
    if (currentPage === getRoute(Route.LOGIN)) {
      defaultPage = getRoute(Route.DASHBOARD);
    }
    dispatch(initializePageSecurity());
    dispatch(setPage(defaultPage));
    metricsEnd();
    changePage(defaultPage as Route);
    dispatch(postInitialize());
  };
};

/**
 * Defines the post-initialization action which is invoked upon successful
 * initialization of all initialization service calls. The post initialization
 * process comprises non-blocking requests which are made in the background
 * subsequent to the first page being rendered and the application being ready
 * for use by the user.
 */
export const postInitialize = () => {
  return async dispatch => {
    info('Application Post Initialization ...');
    dispatch({type: POST_INITIALIZATION_REQUEST});
    try {
      info('Application Post Initialization Complete');
      dispatch({type: POST_INITIALIZATION_COMPLETE});
    } catch (err) {
      error(err);
      dispatch({type: POST_INITIALIZATION_ERROR});
    }
  };
};

/**
 * Invoked any time after initialization has completed (pre, post, etc.) when a
 * specific piece of initialization state needs to be refreshed (such as when an
 * account is added to the users account hierarchy, etc.).
 */
export const refreshInitialization = () => {
  return async(dispatch, getState) => {
    const login = getState().login;
    info('Application Initialization Refresh ...');
    dispatch({type: REFRESH_INITIALIZATION_REQUEST});
    try {
      const accounts = await getSubAccounts(entityId(login?.user.account), true);
      dispatch({type: REFRESH_INITIALIZATION_COMPLETE, accounts});
    } catch (err) {
      dispatch({type: APPLICATION_INITIALIZATION_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked upon application initialization, at which point the EntityObserver is
 * initialized for subsequent entity (asset) change notifications / observations.
 */
export const initializeEntityObserver = async() => {
  if (!EntityObserver.isInitialized) {
    await EntityObserver.initialize({
      authToken            : getToken(),
      appKey               : get('appKey'),
      url                  : get('websocketURL'),
      reconnectionInterval : get('websocketReconnectInterval')
    });
  }
};

/**
 * Initializes the list of assets for realtime change notifications via the
 * AssetObserver API. Upon receiving changes, the change and id of the asset
 * for which the change is to be applied is dispatched and the asset is
 * subsequently updated via the store.
 */
export const initializeAssetObservers = (augmented: Asset[], dispatch: Function): void => {
  EntityObserver.onInitialized(() => {
    const assetIds = augmented.map(({id}) => String(id));
    const observer = (change: GenericObject, id: number) => {
      dispatch(assetUpdated(change as CVSAssetType, id));
    };
    AssetObserver.addObserver(assetIds, observer);
  });
};
